import { useState, useMemo } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { ProTopBar } from '../components/ProTopBar';
import { ProMetricStrip, ProMetric } from '../components/ProMetric';
import { useAthletes, useCoachMe } from '../lib/queries';
import { useRole } from '../lib/role';
import type { AthleteRow, AthleteStatus } from '../lib/api';
import { isPaymentRequired } from '../lib/api';
import { PaywallNotice } from '../components/PaywallNotice';
import styles from './Dashboard.module.css';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function avatarColor(seed: string): string {
  const palette = ['#1d4ed8', '#7c3aed', '#0b8a3e', '#e85d00', '#0891b2', '#d64545', '#be185d', '#a87c00'];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}

function timeAgoIt(iso: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMin = Math.floor((now - then) / 60000);
  if (diffMin < 1) return 'ora';
  if (diffMin < 60) return `${diffMin}m fa`;
  if (diffMin < 60 * 24) return `${Math.floor(diffMin / 60)}h fa`;
  const days = Math.floor(diffMin / 60 / 24);
  if (days === 1) return 'ieri';
  if (days < 8) return `${days}g fa`;
  return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
}

function statusToBadge(s: AthleteStatus): { className: string; dotBg: string; label: string } {
  switch (s) {
    case 'live':
      return { className: styles.statusLive, dotBg: 'var(--green)', label: 'LIVE' };
    case 'attention':
      return { className: styles.statusAttention, dotBg: 'var(--orange)', label: 'ATTENZIONE' };
    case 'deload':
      return { className: styles.statusDeload, dotBg: 'var(--purple)', label: 'DELOAD' };
    case 'active':
    default:
      return { className: styles.statusActive, dotBg: 'var(--green)', label: 'ATTIVO' };
  }
}

type FilterTab = 'all' | 'live' | 'attention' | 'deload';

export default function Atleti() {
  const { role } = useRole();
  const enabled = role === 'coach';
  const { data: athletes, isLoading, isError, error } = useAthletes(enabled);
  const { data: me } = useCoachMe(enabled);
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState<FilterTab>('all');

  const list = useMemo<AthleteRow[]>(() => {
    let out = athletes || [];
    if (tab !== 'all') {
      out = out.filter((a) => a.status === tab);
    }
    const q = query.trim().toLowerCase();
    if (q) {
      out = out.filter(
        (a) =>
          a.name.toLowerCase().includes(q) ||
          (a.program_name?.toLowerCase().includes(q) ?? false) ||
          (a.goal?.toLowerCase().includes(q) ?? false)
      );
    }
    return out;
  }, [athletes, query, tab]);

  if (role !== 'coach') {
    return (
      <>
        <ProTopBar breadcrumb="Studio Pro · Coach" title="Atleti" />
        <div className={styles.empty} style={{ padding: 48 }}>
          Sezione disponibile solo se il tuo profilo include il ruolo Coach.
        </div>
      </>
    );
  }

  if (isError) {
    return (
      <>
        <ProTopBar breadcrumb="Studio Pro · Errore" title="Atleti" />
        {isPaymentRequired(error) ? (
          <PaywallNotice resource="la gestione atleti" />
        ) : (
          <div className={styles.errorBox}>
            Errore caricamento atleti. Ricarica la pagina o controlla la tua connessione.
          </div>
        )}
      </>
    );
  }

  const liveCount = (athletes || []).filter((a) => a.status === 'live').length;
  const attentionCount = (athletes || []).filter((a) => a.status === 'attention').length;
  const deloadCount = (athletes || []).filter((a) => a.status === 'deload').length;
  const total = me?.athletes_count ?? (athletes?.length ?? 0);

  return (
    <>
      <ProTopBar
        breadcrumb={`Studio Pro · ${total} ${total === 1 ? 'atleta attivo' : 'atleti attivi'}`}
        title="Atleti"
      />

      <ProMetricStrip>
        <ProMetric eyebrow="Attivi" value={total} sub={`${liveCount} in sessione ora`} />
        <ProMetric
          eyebrow="Da rivedere"
          value={attentionCount}
          sub="Aderenza sotto soglia"
          variant={attentionCount > 0 ? 'orange' : 'default'}
        />
        <ProMetric eyebrow="In deload" value={deloadCount} sub="Settimana scarico" variant="default" />
        <ProMetric eyebrow="Programmi attivi" value={me?.active_programs ?? 0} sub="Coach" variant="green" />
      </ProMetricStrip>

      <section className={styles.card} style={{ marginTop: 18 }}>
        <header className={styles.cardHeader}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <input
              type="search"
              placeholder="Cerca per nome, programma, goal…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{
                background: 'var(--surface-2)',
                border: '1px solid var(--line)',
                borderRadius: 8,
                padding: '8px 12px',
                fontSize: 13,
                fontFamily: 'var(--sans)',
                minWidth: 280,
                outline: 'none',
              }}
            />
            <span className={styles.cardSub} style={{ marginTop: 0 }}>{list.length} risultati</span>
          </div>
          <div className={styles.seg}>
            {(['all', 'live', 'attention', 'deload'] as FilterTab[]).map((t) => (
              <button
                key={t}
                type="button"
                className={`${styles.segBtn} ${tab === t ? styles.segBtnActive : ''}`}
                onClick={() => setTab(t)}
              >
                {t === 'all' ? 'Tutti' : t === 'live' ? 'In sessione' : t === 'attention' ? 'Attenzione' : 'Deload'}
              </button>
            ))}
          </div>
        </header>
        {isLoading ? (
          <div className={styles.empty}>Caricamento…</div>
        ) : list.length === 0 ? (
          <div className={styles.empty}>
            {(athletes?.length ?? 0) === 0
              ? 'Nessun atleta agganciato. Crea il primo link nutrizionista-coach.'
              : `Nessun risultato per "${query}".`}
          </div>
        ) : (
          <div className={styles.roster}>
            <table className={styles.rosterTable}>
              <thead>
                <tr>
                  <th style={{ width: '28%' }}>Atleta</th>
                  <th>Programma</th>
                  <th>Aderenza 7g</th>
                  <th>Volume sett.</th>
                  <th>PR 30g</th>
                  <th>Ultima</th>
                  <th style={{ width: 40 }} aria-label="Azioni" />
                </tr>
              </thead>
              <tbody>
                {list.map((a) => {
                  const b = statusToBadge(a.status);
                  const adh = a.adherence_7d;
                  const lastLabel = a.status === 'live' ? 'ora' : timeAgoIt(a.last_session_at);

                  return (
                    <tr key={a.id}>
                      <td>
                        <div className={styles.pt}>
                          <div className={styles.ptAv} style={{ background: avatarColor(a.id) }}>
                            {initials(a.name)}
                          </div>
                          <div>
                            <div className={styles.ptName}>{a.name}</div>
                            <div className={styles.ptMeta}>
                              {[a.sex, a.age ? `${a.age}a` : null, a.goal].filter(Boolean).join(' · ') || '—'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`${styles.statusBadge} ${b.className}`}>
                          <span className={styles.statusDot} style={{ background: b.dotBg }} />
                          {b.label}{a.program_name ? ` · ${a.program_name.toUpperCase()}` : ''}
                        </span>
                      </td>
                      <td>
                        <div className={styles.adhCell}>
                          <span className={styles.num}>{adh !== null ? `${adh}%` : '—'}</span>
                          <div className={styles.adhBar}>
                            {adh !== null && (
                              <div style={{ width: `${adh}%`, background: adh >= 80 ? 'var(--green)' : adh >= 60 ? 'var(--orange)' : 'var(--red)' }} />
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={styles.num}>
                          {a.volume_week_kg !== null
                            ? a.volume_week_kg >= 1000
                              ? `${(a.volume_week_kg / 1000).toFixed(1)} t`
                              : `${a.volume_week_kg} kg`
                            : '—'}
                        </span>
                      </td>
                      <td>
                        <span className={`${styles.num} ${a.pr_30d > 0 ? styles.trendUp : styles.numMute}`}>
                          {a.pr_30d > 0 ? `+${a.pr_30d}` : '0'}
                        </span>
                      </td>
                      <td>
                        <span className={`${styles.num} ${a.status === 'live' ? styles.numLive : styles.numMute}`}>{lastLabel}</span>
                      </td>
                      <td>
                        <button type="button" className={styles.rowAction} aria-label="Altre azioni">
                          <MoreHorizontal size={16} strokeWidth={2} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
